// ═══ 검차 신청 제출 배선 — SimpleNewRequest.js 에서 떼어낸 훅(로직 변경 0) ═══
// 제출 상태(submitting/toast/submitError/dup)를 여기서 들고, 방식별 제출 함수를 돌려준다.
// 검증·FormData·중복 선검사는 formLogic.js(순수)에 있다. 새 제출 경로를 만들지 않는다.
// ctx: { t, navigate, mode, ocr, choice, form, photos, orders, ocrNotes, setErrors, setStep, runOcr, timerRef }
import { useRef, useState } from 'react';
import { createOrder } from '../../api';
import { describeSimpleError } from '../../errors';
import { NAV_DELAY, buildBody, findDup, joinNotes, normalizeListingUrl } from './formLogic';

export default function useRequestSubmit(ctx) {
  var t = ctx.t;
  var navigate = ctx.navigate;
  var form = ctx.form;
  var photos = ctx.photos;
  var setErrors = ctx.setErrors;
  var stSub = useState(false); var submitting = stSub[0], setSubmitting = stSub[1];
  var stToast = useState(''); var toast = stToast[0], setToast = stToast[1];
  var stFail = useState(''); var submitError = stFail[0], setSubmitError = stFail[1];
  var stDup = useState(null); var dup = stDup[0], setDup = stDup[1];
  var dupAckRef = useRef('');   // 데스크톱 1페이지 — 같은 차량번호로 두 번째 제출이면 안내를 반복하지 않는다

  var notesOf = function () { return joinNotes(form.memo, ctx.ocrNotes); };
  var dupOf = function (key, value) { return findDup(ctx.orders, key, value); };

  var submit = function (body, dupCtx) {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError('');
    createOrder(body).then(function () {
      setToast(t('evalFormSubmitDone'));
      ctx.timerRef.current = window.setTimeout(function () { navigate('/main/s/requests'); }, NAV_DELAY);
    }).catch(function (e) {
      var code = (e && e.code) || '';
      if (code === 'DUP_LISTING_URL') {
        var dl = dupOf('listing_url', (dupCtx && dupCtx.url) || '');
        setDup({ kind: 'link', id: dl && dl.id });
      } else if (code === 'DUP_CAR_NUMBER') {
        var dc = dupOf('car_number', (dupCtx && dupCtx.plate) || '');
        setDup({ kind: 'car', id: dc && dc.id });
      } else if (code === 'INVALID_URL') {
        setErrors({ url: t('simpleB3LinkInvalid') });
      } else if (code === 'INVALID_PHOTO') {
        setErrors({ photo: t('simpleB4TooMany') });
      } else {
        setSubmitError(describeSimpleError(t, e));
      }
    }).then(function () { setSubmitting(false); });
  };

  /// 사진 + 메모만 제출 — 차종·위치는 빈 값(서버가 첨부 있으면 허용).
  var submitPhotosOnly = function () {
    if (!photos.length) { setErrors({ photo: t('simpleB4NeedPhoto') }); return; }
    setErrors({});
    submit(buildBody({ car_fuel: form.car_fuel, notes: notesOf() }, photos), {});
  };

  var submitChoice = function () {
    if (ctx.ocr === 'failed' || ctx.choice === 'direct') submitPhotosOnly();
    else ctx.runOcr();
  };

  var submitLink = function () {
    var url = normalizeListingUrl(form.listing_url);
    if (!url) { setErrors({ url: t('simpleB3LinkInvalid') }); return; }
    setErrors({});
    var found = dupOf('listing_url', url);
    if (found) { setDup({ kind: 'link', id: found.id }); return; }
    submit(buildBody({ listing_url: url, car_fuel: form.car_fuel, notes: notesOf(),
      location: form.location.trim(), location_detail: form.location_detail.trim() }), { url: url });
  };

  var nextFromVehicle = function () {
    if (!form.car_model.trim()) { setErrors({ model: t('evalFormModelRequired') }); return; }
    setErrors({});
    var plate = form.car_number.trim();
    if (plate) {
      var found = dupOf('car_number', plate);
      // 안내만 하고 진행은 막지 않는다 (최종 판정은 서버) — 닫으면 3단계로 간다.
      if (found) { setDup({ kind: 'car', id: found.id, resume: true }); return; }
    }
    ctx.setStep(3);
  };

  var submitFull = function () {
    var raw = String(form.listing_url || '').trim();
    var url = raw ? normalizeListingUrl(raw) : '';
    if (raw && !url) { setErrors({ url: t('simpleB3LinkInvalid') }); return; }
    if (!form.location.trim() && !url && !photos.length) {
      setErrors({ location: t('evalFormLocationRequired') });
      return;
    }
    setErrors({});
    var plate = form.car_number.trim();
    submit(buildBody({
      car_model: form.car_model.trim(),
      car_detail: form.car_detail.trim(),
      car_number: plate,
      car_year: form.car_year,
      car_mileage: form.car_mileage,
      car_fuel: form.car_fuel,
      seller_phone: form.seller_phone.trim(),
      location: form.location.trim(),
      location_detail: form.location_detail.trim(),
      listing_url: url,
      notes: notesOf()
    }, photos), { url: url, plate: plate });
  };

  /// 데스크톱 1페이지 제출 — 방식별로 기존 제출 함수를 고른다(새 제출 경로를 만들지 않는다).
  var submitDesktop = function () {
    if (ctx.mode === 'link') { submitLink(); return; }
    if (ctx.mode === 'photo' && ctx.choice === 'direct') { submitPhotosOnly(); return; }
    if (!form.car_model.trim()) { setErrors({ model: t('evalFormModelRequired') }); return; }
    setErrors({});
    var plate = form.car_number.trim();
    if (plate && dupAckRef.current !== plate) {
      var found = dupOf('car_number', plate);
      // 안내만 하고 막지 않는다 — 같은 번호로 다시 누르면 그대로 제출(최종 판정은 서버).
      if (found) { dupAckRef.current = plate; setDup({ kind: 'car', id: found.id }); return; }
    }
    submitFull();
  };

  var closeDup = function () {
    var resume = dup && dup.resume;
    setDup(null);
    if (resume) ctx.setStep(3);
  };

  var viewDup = function () {
    var id = dup && dup.id;
    setDup(null);
    navigate(id ? '/main/s/requests/' + id : '/main/s/requests');
  };

  return {
    submitting: submitting, toast: toast, submitError: submitError, dup: dup,
    submitPhotosOnly: submitPhotosOnly, submitChoice: submitChoice, submitLink: submitLink,
    nextFromVehicle: nextFromVehicle, submitFull: submitFull, submitDesktop: submitDesktop,
    closeDup: closeDup, viewDup: viewDup
  };
}
